import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { LoadingButton } from "@mui/lab";
import {
  useAddEventMutation,
  useGetEventById,
} from "src/api/purchasing/events";
import { useGetBranches } from "src/api/generic";
import { useTranslation } from "react-i18next";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import { convertObjectToFormData } from "src/utils";
import { useGetCapitalList } from "src/api/settings/capital";
import { useGetLevelsList } from "src/api/inventory/levels";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationsState } from "src/store/notifications";
import { authState } from "src/store/auth";
import type { IBranchData } from "src/types/generic";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { useGetTreeAccountsList } from "src/api/tree/tree";

interface Props {
  open: boolean;
  handleClose: () => void;
  refetch: Function;
  eventId?: number;
}

const schema = yup.object({
  mode: yup
    .string()
    .required("required")
    .oneOf(["add", "view"] as const),
  branch_id: yup.number().required("Required"),
  branchHasItems: yup.boolean().required("required"),
  direction: yup
    .string()
    .oneOf(["from_inventory", "to_inventory"])
    .required("required"),
  operation: yup.string().required("required"),
  account_id: yup.string().when("operation", {
    is: "expenses",
    then: (v) => v.required("required"),
  }),
  capital_id: yup.string().when("operation", {
    is: "owner",
    then: (v) => v.required("required"),
  }),
  date: yup
    .date()
    .max(new Date(), "Future dates are not allowed")
    .required("required"),
  note: yup.string(),
  items: yup
    .array()
    .of(
      yup.object().shape({
        item_id: yup.number().required("required"),
        item_name: yup.string().required("required"),
        item_sku: yup.string().required("required"),
        item_qnty: yup.string().required("required"),
        unit: yup.string().required("required"),
        unit_cost: yup.string().required("required"),
      }),
    )
    .required("required"),
  amount_total: yup.string().when("branchHasItems", {
    is: false,
    then: (value) => value.required("required"),
  }),
});

type FormInputs = yup.InferType<typeof schema>;

function getBranchHasItems(
  branchId: unknown,
  branches: IBranchData[] | undefined,
): boolean {
  if (branchId === undefined || branchId === null || branchId === "") {
    return true;
  }
  const b = branches?.find((x) => x.id === Number(branchId));
  const fromPackage = b?.active_subscriptions?.package?.has_items;
  const resolved = fromPackage !== undefined ? fromPackage : b?.has_items;
  return resolved !== false;
}

interface Props {
  open: boolean;
  handleClose: () => void;
  refetch: Function;
}

const InventoryEventPopup = ({
  open,
  handleClose,
  refetch,
  eventId,
}: Props) => {
  const { t } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");
  const [files, setFiles] = useState<any>([]);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [openConfirm, setOpenConfirm] = useState(false);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const auth = useRecoilValue(authState);
  const company = auth?.user?.company;

  // apis
  const { mutate, isPending, status, error } = useAddEventMutation();
  const { data: capitalData } = useGetCapitalList();
  const { data: branchesData } = useGetBranches();
  const { data: eventData, isFetching: isFetchingEvent } = useGetEventById(
    `${eventId}`,
  );

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useForm<FormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      mode: "add",
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: "items",
    control,
  });

  const branchId = watch("branch_id");
  const direction = watch("direction");
  const operation = watch("operation");
  const branchHasItems = watch("branchHasItems");

  const { data: accountData } = useGetTreeAccountsList({
    category_system_key: "expenses",
  });

  const { data: itemsData, isFetching } = useGetLevelsList(
    {
      branch_id: +watch("branch_id"),
      date: watch("date")
        ? moment(watch("date")).format("YYYY-MM-DD")
        : undefined,
    },
    { enabled: branchHasItems },
  );
  const mode = watch("mode");

  const getOperations = [
    { value: "owner", label: t("owner") },
    { value: "expenses", label: generalT("expenses") },
  ];

  const onClose = () => {
    handleClose();
    reset({});
  };

  const handleCloseConfirm = () => setOpenConfirm(false);
  const handleOpenConfirm = () => {
    if (mode === "add") {
      setOpenConfirm(true);
    } else {
      onClose();
    }
  };

  // Handle manual changes to the input field
  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string,
  ) => {
    if (selectedItem === null) {
      setInputValue(newInputValue);
    }
  };

  // Handler function when item is selected
  const handleItemSelect = (event: React.SyntheticEvent, newValue: any) => {
    setSelectedItem(newValue);
    if (newValue) {
      append({
        item_id: newValue.id,
        item_name: newValue.name,
        item_sku: newValue.sku,
        item_qnty: "",
        unit: newValue.unit.name,
        unit_cost: newValue.unit_cost,
      });
      setInputValue("");
      setSelectedItem(null);
    }
  };

  const onSubmit = (data: FormInputs) => {
    const items = data.items ?? [];

    // Balance date validation
    if (
      !!company?.start_balance_date &&
      moment(data.date)
        .startOf("day")
        .isBefore(moment(company.start_balance_date).startOf("day"))
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("balanceDateValidationMsg")}: ${
            company?.start_balance_date
          }`,
        },
      ]);
      return;
    }

    if (branchHasItems && data.items.length === 0) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("choose items"),
        },
      ]);
      return;
    }

    const itemsWithTotal = branchHasItems
      ? items.map((item) => ({
          ...item,
          total: Number(item.unit_cost) * Number(item.item_qnty),
        }))
      : [];

    const grandTotal = branchHasItems
      ? itemsWithTotal.reduce((sum, item) => sum + item.total, 0)
      : Number(data.amount_total);

    const payload = {
      type: "inventory",
      branch_id: +data.branch_id,
      operation: data.direction,
      method: data.operation,
      amount: grandTotal as number,
      date: moment(data.date).format("YYYY-MM-DD"),
      ...(data.operation === "expenses" && { chart_account_id: data.account_id }),
      ...(data.operation === "owner" && { capital_id: data.capital_id }),
      ...(data.note && { note: data.note }),
    };

    const formData = convertObjectToFormData(payload);

    if (branchHasItems && itemsWithTotal.length > 0) {
      itemsWithTotal.forEach((item, index: number) => {
        formData.append(`items[${index}][item_id]`, `${item.item_id}`);
        formData.append(`items[${index}][quantity]`, `${item.item_qnty}`);
        formData.append(`items[${index}][unit_cost]`, `${item.unit_cost}`);
        formData.append(`items[${index}][total]`, `${item.total}`);
      });
    }

    // append files
    if (files.length > 0) {
      files.forEach((file: any, index: number) => {
        formData.append(`files[${index}]`, file);
      });
    }

    mutate(formData, {
      onSuccess: () => {
        refetch();
        onClose();
      },
      onError: (error) => {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message:
              error?.data?.errors?.[0].value || generalT("somethingWrong"),
          },
        ]);
      },
    });
  };

  //add success and error handling
  useEffect(() => {
    if (status === "success") {
      refetch();
      onClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0]?.value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  const selectedItemsIds = watch("items")?.map((item) => item.item_id);

  useEffect(() => {
    if (eventData) {
      const data = eventData?.data;
      reset({
        mode: "view",
        branch_id: data.branch?.id,
        direction: data.operation as "to_inventory" | "from_inventory",
        operation: data.method,
        account_id: data.method === "expenses" ? data.account?.id : undefined,
        capital_id: data.method === "owner" ? data.capital?.id : undefined,
        date: moment(data.date, "YYYY-MM-DD").toDate(),
        note: data.note ?? "",
        amount_total: data.amount ?? "",
        branchHasItems: data.items?.length > 0,
        items: data.items.map((item: any) => {
          return {
            item_id: item.item?.id,
            item_name: item.item?.name,
            item_sku: item.item?.sku,
            item_qnty: item.quantity,
            unit: item.item?.unit?.name,
            unit_cost: item.unit_cost ?? 0,
          };
        }),
      });
    }
  }, [eventData]);

  return (
    <Box>
      <Modal open={open}>
        <Stack
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "100%", lg: "737px" },
            bgcolor: "#FFF",
            border: "1px solid #D0D5DD",
            borderRadius: 1,
            boxShadow: 24,
            p: 3,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography fontSize="20px">{t("inventoryEvent")}</Typography>
            <IconButton onClick={handleOpenConfirm} sx={{ p: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>
          {!!eventId && isFetchingEvent ? (
            <Box
              minHeight={"500px"}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap={2}>
                <FormControl fullWidth size="small" error={!!errors.branch_id}>
                  <InputLabel id="demo-simple-select-label">
                    {generalT("branch")}
                  </InputLabel>
                  <Controller
                    name="branch_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        key={field.value}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label={generalT("branch")}
                        disabled={mode === "view"}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                          const hasItems = getBranchHasItems(
                            value,
                            branchesData?.data,
                          );
                          setValue("branchHasItems", hasItems);
                          if (!hasItems) {
                            setValue("items", []);
                          }
                        }}
                      >
                        {branchesData?.data?.map(
                          ({ id, name }: { id: number; name: string }) => (
                            <MenuItem key={id} value={String(id)}>
                              {name}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text" sx={{ color: "#667085" }}>
                    {errors.branch_id?.message}
                  </FormHelperText>
                </FormControl>
                {/* Date */}
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      format={"dd/MM/yyyy"}
                      disabled={mode === "view"}
                      label={`${t("transactionDateRepeat")}`}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: !!errors.date,
                          helperText: errors.date?.message,
                          inputProps: {
                            readOnly: true,
                            onKeyDownCapture: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                            onPasteCapture: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                          },
                        },
                      }}
                    />
                  )}
                />
                {/* Direction */}
                <FormControl
                  size="small"
                  error={!!errors.direction}
                  disabled={mode === "view"}
                >
                  <Controller
                    name="direction"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field} value={field.value ?? ""} row>
                        <FormControlLabel
                          value="to_inventory"
                          control={<Radio size="small" />}
                          label={t("toInventory")}
                        />
                        <FormControlLabel
                          value="from_inventory"
                          control={<Radio size="small" />}
                          label={t("fromInventory")}
                        />
                      </RadioGroup>
                    )}
                  />
                  <FormHelperText>{errors.direction?.message}</FormHelperText>
                </FormControl>

                {/* Operation */}
                {direction && (
                  <FormControl
                    size="small"
                    error={!!errors.operation}
                    disabled={mode === "view"}
                  >
                    <Controller
                      name="operation"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup {...field} row>
                          {getOperations.map((op) => (
                            <FormControlLabel
                              key={op.value}
                              value={op.value}
                              control={<Radio size="small" />}
                              label={op.label}
                            />
                          ))}
                        </RadioGroup>
                      )}
                    />
                    <FormHelperText>{errors.operation?.message}</FormHelperText>
                  </FormControl>
                )}

                {/* Payment */}
                {operation === "expenses" && (
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.account_id}
                    sx={{ mt: 1 }}
                  >
                    <InputLabel id="demo-simple-select-label">
                      {t("expensesAccount")}
                    </InputLabel>
                    <Controller
                      name="account_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          key={field.value}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label={t("expensesAccount")}
                          disabled={mode === "view"}
                        >
                          {accountData?.data.map((account: any) => {
                            return (
                              <MenuItem key={account.id} value={account.id}>
                                {account.name}
                              </MenuItem>
                            );
                          })}
                          {accountData?.data?.length === 0 ? (
                            <Typography align="center" color={"textSecondary"}>
                              {generalT("noData")}
                            </Typography>
                          ) : null}
                        </Select>
                      )}
                    />
                    <FormHelperText id="my-helper-text">
                      {errors.account_id?.message}
                    </FormHelperText>
                  </FormControl>
                )}

                {/* Account (Expenses only) */}
                {operation === "owner" && (
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.capital_id}
                    sx={{ mt: 1 }}
                  >
                    <InputLabel id="demo-simple-select-label">
                      {t("owner")}
                    </InputLabel>
                    <Controller
                      name="capital_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          key={field.value}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label={"owner"}
                          disabled={mode === "view"}
                        >
                          {capitalData?.data.map(({ id, name }: any) => {
                            return (
                              <MenuItem key={id} value={`${id}`}>
                                {name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      )}
                    />
                    <FormHelperText id="my-helper-text">
                      {errors.capital_id?.message}
                    </FormHelperText>
                  </FormControl>
                )}

                {branchHasItems ? (
                  <>
                    <Typography fontWeight={600} lineHeight={"16.8px"}>
                      {t("items")}
                    </Typography>

                    {fields.map((itemField, index) => {
                      const itemRowErrors = errors.items?.[index] as
                        | { item_qnty?: { type?: string; message?: string } }
                        | undefined;
                      const itemQntyError = itemRowErrors?.item_qnty;
                      const itemTotal =
                        Number(watch("items")?.[index]?.unit_cost) *
                        Number(watch("items")?.[index]?.item_qnty);
                      return (
                        <Stack
                          key={itemField.id}
                          padding={"8px 16px"}
                          borderRadius={1.5}
                          border={"1px solid #F0F0F0"}
                          direction={"row"}
                          spacing={"10px"}
                          alignItems={"center"}
                          flexWrap="wrap"
                        >
                          {/* item_name */}
                          <Stack spacing={0.2} paddingX={2} width={"100px"}>
                            <Typography
                              fontWeight={400}
                              color={"#667085"}
                              lineHeight={"24px"}
                              fontSize={"14px"}
                            >
                              {t("name")}
                            </Typography>
                            <Typography
                              fontWeight={700}
                              color={"#667085"}
                              lineHeight={"24px"}
                              fontSize={"14px"}
                            >
                              {itemField.item_name}
                            </Typography>
                          </Stack>

                          <Stack spacing={0.2} paddingX={2} width={"90px"}>
                            <Typography
                              fontWeight={400}
                              color={"#667085"}
                              lineHeight={"24px"}
                              fontSize={"14px"}
                            >
                              {t("sku")}
                            </Typography>
                            <Typography
                              fontWeight={700}
                              color={"#667085"}
                              lineHeight={"24px"}
                              fontSize={"14px"}
                            >
                              {itemField.item_sku}
                            </Typography>
                          </Stack>
                          {/* item_qnty */}
                          <Box minWidth={100}>
                            <Controller
                              name={`items.${index}.item_qnty`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  type="number"
                                  variant="outlined"
                                  {...field}
                                  error={!!itemQntyError}
                                  helperText={
                                    itemQntyError?.type === "itemQntyMax"
                                      ? t("cannotExceedItemLevel")
                                      : generalT(itemQntyError?.message ?? "")
                                  }
                                  size="small"
                                  fullWidth
                                  sx={{ width: "200px" }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        {itemField.unit}
                                      </InputAdornment>
                                    ),
                                  }}
                                  disabled={mode === "view"}
                                />
                              )}
                            />
                          </Box>

                          <Stack spacing={0.2} paddingX={3}>
                            <Typography
                              fontWeight={400}
                              color={"#667085"}
                              lineHeight={"24px"}
                              fontSize={"14px"}
                            >
                              {t("total")}
                            </Typography>
                            <Typography
                              fontWeight={700}
                              color={"#667085"}
                              lineHeight={"24px"}
                              fontSize={"14px"}
                            >
                              {itemTotal.toFixed(2)}
                            </Typography>
                          </Stack>

                          {/* remove item */}
                          <Box
                            display={"flex"}
                            justifyContent={"flex-end"}
                            alignItems={"center"}
                            sx={{ flexGrow: 1 }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => {
                                remove(index);
                              }}
                            >
                              <img
                                src={DeleteIcon}
                                alt=""
                                width={"16px"}
                                style={{ cursor: "pointer" }}
                              />
                            </IconButton>
                          </Box>
                        </Stack>
                      );
                    })}

                    {mode === "add" && (
                      <Autocomplete
                        size="small"
                        disablePortal
                        id="combo-box-demo"
                        options={
                          watch("date")
                            ? (itemsData?.data.filter(
                                (item: any) =>
                                  !selectedItemsIds?.includes(item.id),
                              ) ?? [])
                            : []
                        }
                        sx={{ width: 255 }}
                        getOptionLabel={(option: any) => option.name}
                        value={selectedItem}
                        onChange={handleItemSelect}
                        inputValue={inputValue}
                        onInputChange={handleInputChange}
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label={t("searchAndAddItem")}
                          />
                        )}
                        noOptionsText={generalT(
                          isFetching && watch("date") ? "loading" : "noOption",
                        )}
                      />
                    )}
                  </>
                ) : (
                  <Controller
                    name="amount_total"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        type="number"
                        size="small"
                        fullWidth
                        label={t("amount")}
                        error={!!errors.amount_total}
                        helperText={
                          errors.amount_total?.message
                            ? generalT(String(errors.amount_total.message))
                            : undefined
                        }
                        inputProps={{ step: "0.01", min: 0 }}
                        disabled={mode === "view"}
                      />
                    )}
                  />
                )}

                {/* Note */}
                <Controller
                  name="note"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      label={t("notesOptional")}
                      fullWidth
                      multiline
                      rows={3}
                      sx={{ mt: 1 }}
                      disabled={mode === "view"}
                    />
                  )}
                />

                {mode === "add" && (
                  <FilePondUploader
                    onUpload={(e) => setFiles(e)}
                    maxFiles={3}
                    maxFileSize={3}
                    acceptedFileTypes={["image/*", "application/pdf"]}
                  />
                )}
              </Stack>
              {mode === "add" && (
                <Stack direction="row" spacing={2} mt={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleOpenConfirm}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    fullWidth
                    loading={isPending}
                  >
                    Submit
                  </LoadingButton>
                </Stack>
              )}
            </form>
          )}
        </Stack>
      </Modal>
      {/* confirm close popup */}
      <ConfirmPopup
        open={openConfirm}
        handleClose={handleCloseConfirm}
        handleConfirm={onClose}
        title={generalT("discard")}
        subtitle={generalT("areYouSureToDiscardChanges")}
        confirmBtnText={generalT("confirm")}
      />
    </Box>
  );
};

export default InventoryEventPopup;
